/**
 * Creative Director — the orchestrator.
 *
 * direct(brief) takes a creative brief and produces a complete, coherent
 * CreativeDirection: pure decisions, zero rendering, zero markup. It is
 * the only module that calls the other twelve; each stays independently
 * usable. Coherence comes from ordering — every choice narrows the next:
 *
 *   audience → mood → brand style → trends → movement → focus
 *     → hierarchy → composition → negative space → balance
 *     → storytelling → layout reasoning
 *
 * Deterministic per seed (same brief + seed ⇒ same direction), which makes
 * A/B evaluation of future OpenAI-made choices meaningful.
 */

import { getAudience } from './audienceEngine';
import { getMood } from './moodEngine';
import { getBrandStyle } from './brandStyleEngine';
import { pickTrends } from './trendEngine';
import { chooseFlow } from './movementEngine';
import { decideFocus } from './focusEngine';
import { rankObjects, validateHierarchy } from './visualHierarchyEngine';
import { composeIntent } from './compositionEngine';
import { planNegativeSpace } from './negativeSpaceEngine';
import { planBalance } from './balanceEngine';
import { planStory } from './storytellingEngine';
import { reasonLayout } from './layoutReasoningEngine';

export const DIRECTION_VERSION = 1;

/** Deterministic PRNG (mulberry32) — kept local so this library stays standalone. */
export const createRng = (seed = Date.now()) => {
  let a = seed >>> 0;
  return () => {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const pick = (rng, arr) => arr[Math.floor(rng() * arr.length)];

/**
 * Produce creative direction for a brief.
 *
 * brief = {
 *   audience   (required) — audienceEngine id
 *   mood?      — pin a mood; must be in the audience's compatible list
 *                unless force: true
 *   brandStyle? — pin a style; same compatibility rule
 *   seed?      — RNG seed for reproducibility
 *   force?     — allow pins outside the audience's compatibility lists
 * }
 */
export const direct = ({ audience: audienceId, mood: moodPin, brandStyle: stylePin, seed = Date.now(), force = false } = {}) => {
  const audience = getAudience(audienceId);
  const rng = createRng(seed);

  if (moodPin && !force && !audience.moods.includes(moodPin)) {
    throw new Error(
      `Mood "${moodPin}" clashes with a ${audience.name} audience (compatible: ${audience.moods.join(', ')}). Pass force: true to override.`
    );
  }
  if (stylePin && !force && !audience.brandStyles.includes(stylePin)) {
    throw new Error(
      `Brand style "${stylePin}" clashes with a ${audience.name} audience (compatible: ${audience.brandStyles.join(', ')}). Pass force: true to override.`
    );
  }

  const mood = getMood(moodPin || pick(rng, audience.moods));
  const style = getBrandStyle(stylePin || pick(rng, audience.brandStyles));
  const trends = pickTrends(style.id, mood.id, rng, 2);

  // Energy blends who is looking (audience) with how it should feel (mood).
  const energy = Number((audience.energy * 0.4 + mood.energy * 0.6).toFixed(2));
  const flow = chooseFlow(energy, rng);

  const focus = decideFocus({ audience, mood, rng });
  const ranks = rankObjects({ focus, style });
  const hierarchyCheck = validateHierarchy(ranks);
  const composition = composeIntent({ audience, mood, style, focus, flow, trends, rng });
  const negativeSpace = planNegativeSpace({ style, mood });
  const balance = planBalance({ mood, flow });
  const storytelling = planStory({ focus, mood, audience });
  const layoutReasoning = reasonLayout({ flow, composition, focus, audience });

  return {
    audience: {
      id: audience.id, name: audience.name,
      readingHabit: audience.readingHabit, energy: audience.energy,
      factoryCategory: audience.factoryCategory,
    },
    mood: {
      id: mood.id, name: mood.name,
      energy: mood.energy, warmth: mood.warmth,
      sophistication: mood.sophistication, playfulness: mood.playfulness,
      contrast: mood.contrast, spacingFeel: mood.spacingFeel,
      paletteThemes: [...mood.paletteThemes],
    },
    brandStyle: {
      id: style.id, name: style.name,
      typographyEmphasis: style.typographyEmphasis,
      whitespaceBias: style.whitespaceBias,
      layerDepth: style.layerDepth,
      feel: { ...style.feel },
      typographyTheme: style.typographyTheme,
    },
    trend: {
      selected: trends.map(({ id, name, effects }) => ({ id, name, effects: { ...effects } })),
    },
    movement: {
      flow: flow.id, name: flow.name,
      path: [...flow.path], endpoint: flow.endpoint,
      energy,
    },
    focus,
    visualHierarchy: { ranks, valid: hierarchyCheck.valid, errors: hierarchyCheck.errors },
    composition,
    negativeSpace,
    balance,
    storytelling,
    layoutReasoning,
    meta: { seed, version: DIRECTION_VERSION },
  };
};
